import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {BehaviorSubject, map, Observable} from "rxjs";
import {LoaderService} from "./loader.service";
import {ActionResult, Category} from "@model";
import {EnvironmentService} from "./environment.service";

@Injectable({ providedIn:'root' })
export class CategoriesService {

  static readonly CATEGORY_NOT_USED_TYPE_ID: number = 3;

  private static _currentCategories: BehaviorSubject<Category[] | undefined> = new BehaviorSubject<Category[] | undefined>(undefined);

  get currentCategories(): BehaviorSubject<Category[] | undefined> {
    return CategoriesService._currentCategories;
  }

  constructor(private http: HttpClient, private loaderService: LoaderService, private environmentService:EnvironmentService) {
  }

  /**
   * @param organiserId - either for some organiser in case of admin user of for the current organiser in case of
   * logged in organiser(in this case we don't have to send organiserId since it is known on the backend from the
   * session)
   * @param filterEditable -Set the flag to false only if editing the category list is disabled,
   * here to enable selection of the default category.
   */
  findCategoriesList(organiserId: number | undefined, filterEditable: boolean = true): Observable<Category[] | null> {
    return this.http.post<ActionResult<Category[]>>(this.environmentService.getApiUrl() + "/api/category/fetch/organiserid", organiserId)
      .pipe(
        map((value: ActionResult<Category[]>) => {
          if (value && value.result) {
            return filterEditable ? this.filterEditableCategories(value.result ?? []) : value.result;
          } else {
            return null;
          }
        })
      );
  }

  /**
   * @param organiserId - either for some organiser in case of admin user of for the current organiser in case of
   * logged in organiser(in this case we don't have to send organiserId since it is known on the backend from the
   * session)
   * @param filterEditable -Set the flag to false only if editing the category list is disabled,
   * here to enable selection of the default category.
   */
  findCategories(organiserId: number | undefined, onSuccess?: () => void, onError?: () => void, filterEditable: boolean = true): void {
    this.http.post<ActionResult<Category[]>>(this.environmentService.getApiUrl() + "/api/category/fetch/organiserid", organiserId).subscribe((actionResult: ActionResult<Category[]>) => {
      if (actionResult.resultCode !== -1) {
        this.currentCategories.next(filterEditable ? this.filterEditableCategories(actionResult.result ?? []) : actionResult.result);
        if (onSuccess) onSuccess();
      } else {
        if (onError) onError();
      }
    });
  }

  updateCategory(organiserId: number | undefined, category: Category, onSuccess: () => void, onError: () => void): void {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>
    (this.environmentService.getApiUrl() + (category.id ? "/api/category/update" : "/api/category/add"), category)
      .subscribe((actionResult: ActionResult<boolean>) => {
        if (actionResult.resultCode !== -1) {
          this.findCategories(organiserId, () => {
            this.loaderService.dismissLoading();
            onSuccess();
          }, () => {
            this.loaderService.dismissLoading();
            onError();
          });
        } else {
          this.loaderService.dismissLoading();
          onError();
        }
      });
  }

  deleteCategory(organiserId: number | undefined, category: Category, onSuccess: () => void, onError: () => void): void {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>
    (this.environmentService.getApiUrl() + "/api/category/delete/id", category.id)
      .subscribe((actionResult: ActionResult<boolean>) => {
        if (actionResult.resultCode !== -1) {
          this.findCategories(organiserId, () => {
            this.loaderService.dismissLoading();
            onSuccess();
          }, () => {
            this.loaderService.dismissLoading();
            onError();
          });
        } else {
          this.loaderService.dismissLoading();
          onError();
        }
      });
  }

  filterEditableCategories(category: Category[]): Category[] {
    return [...category.filter((category: Category) => !(category.categoryType === CategoriesService.CATEGORY_NOT_USED_TYPE_ID))];
  }
}
